import './AjaxSelect.less';
import React, {useState, useEffect, useRef} from 'react';
import Request from '../../api/Request';
import Dropdown from '../../components/dropdown/Dropdown';
import Icon from '../../components/icon/Icon';
import Spinner from '../../components/spinner/Spinner';
import {translate} from '../../i18n';

export default function AjaxSelect({
    defaultValue = null,
    value: forcedValue,
    isSingle = false,
    clearable = false,
    disabled = false,
    prompt = '',
    dropdownHorizontalAlignment = 'left',
    ItemsSearchParcel,
    ItemsFetchParcel,
    renderOption,
    renderSelection,
    onChange = (value) => console.log('(AjaxSelect) onChange is omitted:', value)
}) {

    if (defaultValue === null && !isSingle) {
        defaultValue = [];
    }

    const KEYWORD_INPUT_PADDING = 2;

    const [isInitialized, setIsInitialized] = useState(false);

    const [selectedItems, setSelectedItems] = useState([]);

    const [items, setItems] = useState([]);

    const [hasMore, setHasMore] = useState(true);

    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState(null);

    const [keyword, setKeyword] = useState('');

    const [lastPage, setLastPage] = useState(0);

    const [dropdownShown, setDropdownShown] = useState(false);

    const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);

    const [keywordInputWidth, setKeywordInputWidth] = useState(KEYWORD_INPUT_PADDING);

    const [keywordTimer, setKeywordTimer] = useState(null);

    const searchItems = (page) => {
        setIsLoading(true);
        setHasMore(true);

        Request.add(new ItemsSearchParcel(keyword, page))
            .then(data => {
                const prevItems = page === 0 ? [] : items;
                const nextItems = prevItems.concat(data.items);
                const hasMore = data.hasOwnProperty('total')
                    ? data.total > nextItems.length
                    : data.items.length > 0;

                setLastPage(page);
                setHasMore(hasMore);
                setItems(nextItems);
            })
            .catch(error => {
                setError(error.getMessage());
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        setSelectedItems(items.filter(t => defaultValue === t.id || defaultValue.includes(t.id)));
    }, []);

    useEffect(() => {
        if (isSingle ? defaultValue === null : defaultValue.length === 0) {
            setIsInitialized(true);
            setSelectedItems([]);
            return;
        }

        setIsLoading(true);

        Request.add(new ItemsFetchParcel(isSingle ? [defaultValue] : defaultValue))
            .then(data => {
                setIsInitialized(true);
                setSelectedItems(data.items);
            })
            .catch(error => {
                setIsInitialized(true);
                setError(error.getMessage());
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (forcedValue === undefined) {
            return;
        }

        if (isSingle ? forcedValue === null : forcedValue.length === 0) {
            setSelectedItems([]);
            return;
        }

        setIsLoading(true);

        Request.add(new ItemsFetchParcel(isSingle ? [forcedValue] : forcedValue))
            .then(data => {
                setSelectedItems(data.items);
            })
            .catch(error => {
                setError(error.getMessage());
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [forcedValue]);

    useEffect(() => {
        if (!dropdownShown) {
            return;
        }

        if (keywordInputRef.current.value !== keyword) {
            keywordInputRef.current.value = keyword;
        }

        searchItems(0);
    }, [keyword]);

    useEffect(() => {
        if (dropdownShown) {
            searchItems(0);
        } else {
            setItems([]);
        }
    }, [dropdownShown]);

    const onScroll = (ev) => {
        if (ev.target.clientHeight + ev.target.scrollTop < ev.target.scrollHeight - 2) {
            return;
        }

        if (hasMore && !isLoading) {
            searchItems(lastPage + 1);
        }
    };

    const blur = () => {
        setDropdownShown(false);
        setKeyword('');

        if (keywordInputRef.current !== null) {
            keywordInputRef.current.value = '';
            keywordInputRef.current.blur();
        }
    };

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (isSingle) {
            if (selectedItems.length > 0) {
                onChange(selectedItems[0].id);
                return;
            }

            onChange(null);
            return;
        }

        onChange(selectedItems.map(t => t.id));
    }, [selectedItems]);

    const handleSelect = (item) => {
        const foundItem = selectedItems.find(t => t.id === item.id);

        if (isSingle) {
            if (!foundItem) {
               setSelectedItems([item]);
            }
            blur();
            return;
        }

        if (!foundItem) {
            setSelectedItems(selectedItems.concat([item]));
        }
    };

    const handleDeselect = (item) => {
        const foundItem = selectedItems.find(t => t.id === item.id);

        if (isSingle) {
            if (foundItem) {
                setSelectedItems([]);
            }
            return;
        }

        if (foundItem) {
            setSelectedItems(selectedItems.filter(t => t !== foundItem));
        }
    };

    const handleDeselectAll = () => {
        if (selectedItems.length === 0) {
            return;
        }

        setSelectedItems([]);
    };

    const handleKeyDown = (ev) => {
        if (ev.key === 'Tab') {
            setDropdownShown(false);
            return;
        }

        if (ev.key === 'Enter') {
            ev.preventDefault();
            if (items[focusedOptionIndex] !== undefined) {
                handleSelect(items[focusedOptionIndex]);
            }
            return;
        }

        if (ev.key === 'ArrowUp') {
            ev.preventDefault();
            setFocusedOptionIndex(Math.max(0, focusedOptionIndex - 1));
            return;
        }

        if (ev.key === 'ArrowDown') {
            ev.preventDefault();
            setFocusedOptionIndex(Math.min(items.length - 1, focusedOptionIndex + 1));
            return;
        }

        if (keyword === '' && selectedItems.length > 0 && ev.key === 'Backspace') {
            ev.preventDefault();
            handleDeselect(selectedItems[selectedItems.length - 1]);
        }
    };

    const dropdownRef = useRef(null);
    const scrollViewRef = useRef(null);
    const optionsUlRef = useRef(null);
    const inlineRef = useRef(null);
    const inlineUlRef = useRef(null);
    const keywordLiRef = useRef(null);
    const keywordInputRef = useRef(null);

    const measureKeywordInputWidth = (keyword) => {
        const liStyle = window.getComputedStyle(keywordLiRef.current);
        const liMargin = parseFloat(liStyle.marginLeft) + parseFloat(liStyle.marginRight);

        const context2d = document.createElement('canvas').getContext('2d');
        const inputStyle = window.getComputedStyle(keywordInputRef.current);
        context2d.font = inputStyle.font;

        return Math.min(
            inlineUlRef.current.clientWidth - liMargin,
            context2d.measureText(keyword).width + KEYWORD_INPUT_PADDING
        );
    };

    const updateDropdownScroll = () => {
        if (!optionsUlRef.current) {
            return;
        }

        const focusedOptionEl = optionsUlRef.current.children[focusedOptionIndex];
        if (!focusedOptionEl) {
            return;
        }

        const optionHeight = focusedOptionEl.offsetHeight;
        const optionTop = focusedOptionEl.offsetTop - scrollViewRef.current.offsetTop;
        const svHeight = scrollViewRef.current.clientHeight;
        const svTop = scrollViewRef.current.scrollTop;

        if (svTop < optionTop - (svHeight - optionHeight)) {
            scrollViewRef.current.scrollTop = optionTop - (svHeight - optionHeight);
            return;
        }

        if (svTop > optionTop) {
            scrollViewRef.current.scrollTop = optionTop;
        }
    };

    const delaySetKeyword = (keyword) => {
        setKeywordInputWidth(measureKeywordInputWidth(keyword));

        if (keywordTimer !== null) {
            clearTimeout(keywordTimer);
        }

        setKeywordTimer(setTimeout(() => {
            setKeyword(keyword);
            setKeywordTimer(null);
        }, 500));
    };

    useEffect(() => {
        if (hasMore &&
            scrollViewRef.current !== null &&
            scrollViewRef.current.scrollHeight === scrollViewRef.current.clientHeight
        ) {
            searchItems(lastPage + 1);
        }
    }, [items]);

    return (
        <div className={'ajax-select' + (isSingle ? ' single' : '') + (disabled ? ' disabled' : '')}>
            <div
                className="inline"
                ref={inlineRef}
                onClick={() => {
                    if (disabled) {
                        return;
                    }
                    keywordInputRef.current.focus();
                    setDropdownShown(true);
                }}
            >
                <div className="selections">
                    {
                        (keywordInputRef.current === null || keywordInputRef.current.value === '') &&
                        selectedItems.length === 0 &&
                        <div className="prompt">
                            <div className="text">
                                {isLoading ? translate('Loading...') : prompt}
                            </div>
                        </div>
                    }
                    <ul ref={inlineUlRef}>
                        {
                            selectedItems.map((item) => {
                                return (
                                    <li key={item.id}>
                                        {
                                            isSingle ?
                                                <div className="single-selection">
                                                    <div className="content">{(renderSelection || renderOption)(item)}</div>
                                                </div>
                                                :
                                                <div className="selection">
                                                    <div className="content">{(renderSelection || renderOption)(item)}</div>
                                                    {
                                                        !disabled &&
                                                        <button
                                                            type="button"
                                                            className="remove-button"
                                                            tabIndex={-1}
                                                            onClick={ev => {
                                                                ev.stopPropagation();
                                                                handleDeselect(item);
                                                            }}
                                                        >
                                                            <Icon name="times"/>
                                                        </button>
                                                    }
                                                </div>
                                        }
                                    </li>
                                );
                            })
                        }
                        {
                            !disabled &&
                            <li
                                ref={keywordLiRef}
                                className={!dropdownShown ? 'zero-space' : ''}
                            >
                                <input
                                    ref={keywordInputRef}
                                    onKeyDown={handleKeyDown}
                                    type="text"
                                    className="keyword-input"
                                    defaultValue={keyword}
                                    onKeyUp={ev => delaySetKeyword(ev.target.value)}
                                    onPaste={ev => delaySetKeyword(ev.target.value)}
                                    onChange={ev => delaySetKeyword(ev.target.value)}
                                    style={{width: keywordInputWidth + 'px'}}
                                />
                            </li>
                        }
                    </ul>
                </div>
                <div className="actions">
                    {
                        isLoading
                            ?
                            <Spinner/>
                            :
                            (
                                error !== null
                                    ?
                                    <button
                                        type="button"
                                        className="error-button"
                                        aria-label={error}
                                        aria-controls="tooltip-click-to-hold"
                                        tabIndex={-1}
                                    >
                                        <Icon name="caution-circle"/>
                                    </button>
                                    :
                                    (
                                        !disabled && (
                                            clearable && selectedItems.length > 0
                                                ?
                                                <button
                                                    type="button"
                                                    className="clear-button"
                                                    onClick={ev => {
                                                        ev.stopPropagation();
                                                        handleDeselectAll();
                                                    }}
                                                    tabIndex={-1}
                                                >
                                                    <Icon name="times"/>
                                                </button>
                                                :
                                                <button
                                                    type="button"
                                                    className="caret-button"
                                                    tabIndex={-1}
                                                >
                                                    <Icon name="caret-down"/>
                                                </button>
                                        )
                                    )
                            )
                    }
                </div>
            </div>
            {
                dropdownShown &&
                <Dropdown
                    name="ajax-select"
                    opener={inlineRef.current}
                    minWidth="anchorWidth"
                    horizontalAlignment={dropdownHorizontalAlignment}
                    close={blur}
                    onRefreshDisplay={updateDropdownScroll}
                >
                    {
                        (contentViewableMaxHeight) => {
                            return <div ref={dropdownRef}>
                                <div className="dropdown-scroll-container">
                                    <div
                                        className="dropdown-scroll-view"
                                        style={{maxHeight: contentViewableMaxHeight + 'px'}}
                                        ref={scrollViewRef}
                                        onScroll={onScroll}
                                    >
                                        {
                                            items.length === 0 &&
                                            <div className="empty-placeholder">
                                                {
                                                    isLoading
                                                        ?
                                                        <>
                                                            <Spinner/>
                                                            <span>{translate('Loading...')}</span>
                                                        </>
                                                        :
                                                        <span>{translate('No options')}</span>
                                                }
                                            </div>
                                        }
                                        {
                                            items.length > 0 &&
                                            <ul ref={optionsUlRef}>
                                                {
                                                    items.map((item, index) => {

                                                        const selected = !!selectedItems.find(t => t.id === item.id);
                                                        const focused = focusedOptionIndex === index;
                                                        const className
                                                            = (selected ? ' selected' : '')
                                                            + (focused ? ' focused' : '');

                                                        return (
                                                            <li
                                                                key={item.id}
                                                                className={className}
                                                                onClick={() => {
                                                                    keywordInputRef.current.focus();
                                                                    handleSelect(item);
                                                                }}
                                                                onMouseEnter={() => setFocusedOptionIndex(index)}
                                                            >
                                                                <div className="option">
                                                                    {
                                                                        renderOption(item, {
                                                                            selected
                                                                        })
                                                                    }
                                                                </div>
                                                                {
                                                                    !isSingle &&
                                                                    <button
                                                                        type="button"
                                                                        className="remove-button"
                                                                        onClick={(ev) => {
                                                                            ev.stopPropagation();
                                                                            keywordInputRef.current.focus();
                                                                            handleDeselect(item);
                                                                        }}
                                                                    >
                                                                        {translate('Remove')}
                                                                    </button>
                                                                }
                                                            </li>
                                                        );
                                                    })
                                                }
                                            </ul>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    }
                </Dropdown>
            }
        </div>
    );
}
